import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import ClientSelector from '../../piece-registration/components/ClientSelector';
import EmployeeSelector from '../../piece-registration/components/EmployeeSelector';
import PieceDetailsForm from '../../piece-registration/components/PieceDetailsForm';
import RendimientoSection from '../../piece-registration/components/RendimientoSection';
import FotografiaSection from '../../piece-registration/components/FotografiaSection';
import { ERPCard, ERPButton } from '../../../components/ui/ERPLayout';
import { supabase } from '../../../lib/supabase';
import { calcularMerma, calcularRendimiento, getRendimientoStatus, rendimientoService } from '../../../services/rendimientoService';
import { calcularTarifa, calcularCosteSala, getTarifaDescripcion } from '../../../utils/tarifaCalculation';

// ─── Helper: format date as YYYYMMDD ─────────────────────────────────────────
const formatDateYYYYMMDD = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const yyyy = d?.getFullYear();
  const mm = String(d?.getMonth() + 1)?.padStart(2, '0');
  const dd = String(d?.getDate())?.padStart(2, '0');
  return `${yyyy}${mm}${dd}`;
};

const DEFAULT_LOTE_INTERNO = 'LDD-3 / 690';

const inputCls = 'w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors';
const labelCls = 'block text-[17px] font-oranienbaum text-white mb-1.5';

const LoncheadosEditModal = ({ loncheado, clientes, empleados, productos, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [generatingLote, setGeneratingLote] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);

  const [formData, setFormData] = useState({
    clientId: null,
    employeeId: null,
    productId: null,
    weight: '',
    cutType: 'NORMAL',
    registrationDate: new Date()?.toISOString()?.split('T')?.[0],
    registrationTime: new Date()?.toTimeString()?.slice(0, 5),
    observaciones: '',
    pesoAprovechado: '',
    fotos: [],
    loteInterno: DEFAULT_LOTE_INTERNO,
    loteExterno: '',
    emplatado: false,
    numeroBlisteres: 0,
  });

  const [errors, setErrors] = useState({});

  // Load existing record data into form
  useEffect(() => {
    if (!loncheado) return;

    let existingFotos = [];
    const fotoUrl = loncheado?.fotoUrl || loncheado?.foto_url;
    if (fotoUrl) {
      try {
        const parsed = JSON.parse(fotoUrl);
        existingFotos = Array.isArray(parsed)
          ? parsed?.map((url, i) => ({ url, name: `Foto ${i + 1}`, remote: true }))
          : [{ url: fotoUrl, name: 'Foto 1', remote: true }];
      } catch {
        existingFotos = [{ url: fotoUrl, name: 'Foto 1', remote: true }];
      }
    }

    const createdAt = loncheado?.createdAt || loncheado?.created_at
      ? new Date(loncheado?.createdAt || loncheado?.created_at)
      : new Date();

    const pesoInicial = loncheado?.pesoInicial ?? loncheado?.peso_inicial ?? loncheado?.peso ?? loncheado?.peso_loncheado ?? '';
    const pesoAprovechado = loncheado?.pesoAprovechado ?? loncheado?.peso_aprovechado ?? '';

    // Lote interno: use saved value if exists, otherwise default
    const savedLoteInterno = loncheado?.lote_interno || loncheado?.loteInterno;
    const loteInternoValue = savedLoteInterno || DEFAULT_LOTE_INTERNO;

    // Lote externo: use saved value if exists (never overwrite)
    const savedLoteExterno = loncheado?.lote_externo || loncheado?.loteExterno || '';

    const savedNumeroBlisteres = loncheado?.numero_blisteres != null ? loncheado?.numero_blisteres : 0;

    setFormData({
      clientId: loncheado?.clienteId || loncheado?.cliente_id || null,
      employeeId: loncheado?.empleadoId || loncheado?.empleado_id || null,
      productId: loncheado?.productoId || loncheado?.producto_id || null,
      weight: pesoInicial != null ? String(pesoInicial) : '',
      cutType: loncheado?.tipo || 'NORMAL',
      registrationDate: createdAt?.toISOString()?.split('T')?.[0],
      registrationTime: createdAt?.toTimeString()?.slice(0, 5),
      observaciones: loncheado?.observaciones || '',
      pesoAprovechado: pesoAprovechado != null ? String(pesoAprovechado) : '',
      fotos: existingFotos,
      loteInterno: loteInternoValue,
      loteExterno: savedLoteExterno,
      emplatado: loncheado?.emplatado || false,
      numeroBlisteres: savedNumeroBlisteres,
    });

    // If no lote externo saved, generate one based on registration date
    if (!savedLoteExterno) {
      generateLoteExternoForDate(createdAt);
    }
  }, [loncheado?.id]);

  const generateLoteExternoForDate = async (date) => {
    try {
      setGeneratingLote(true);
      const dateStr = formatDateYYYYMMDD(date || new Date());
      const { data, error: rpcError } = await supabase?.rpc('get_next_lote_externo', { p_date: dateStr });
      if (rpcError) throw rpcError;
      if (data) {
        setFormData(prev => ({ ...prev, loteExterno: data }));
      }
    } catch (err) {
      console.warn('Error generating lote externo:', err?.message);
      const dateStr = formatDateYYYYMMDD(date || new Date());
      setFormData(prev => ({ ...prev, loteExterno: `LDD-${dateStr}-001` }));
    } finally {
      setGeneratingLote(false);
    }
  };

  const handleChange = (key, val) => {
    setFormData(prev => ({ ...prev, [key]: val }));
    if (errors?.[key]) setErrors(prev => ({ ...prev, [key]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.clientId) newErrors.clientId = 'Seleccione un cliente';
    if (!formData?.employeeId) newErrors.employeeId = 'Seleccione un empleado';
    if (!formData?.weight || parseFloat(formData?.weight) <= 0) newErrors.weight = 'Peso válido requerido';
    else if (parseFloat(formData?.weight) > 50) newErrors.weight = 'Máximo 50 kg';
    if (!formData?.cutType) newErrors.cutType = 'Seleccione tipo de gestión';
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  // Determine if selected client is "Las Dos Doncellas"
  const clienteSeleccionado = clientes?.find(c => c?.id === formData?.clientId);
  const isLasDosCliente = clienteSeleccionado?.nombre?.toLowerCase()?.includes('dos doncellas') || false;

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    setBtnLoading(true);
    setError(null);

    try {
      const pesoValue = parseFloat(formData?.weight);
      const pesoAprovechadoValue = formData?.pesoAprovechado ? parseFloat(formData?.pesoAprovechado) : null;
      const mermaValue = calcularMerma(formData?.weight, formData?.pesoAprovechado);
      const rendimientoValue = calcularRendimiento(formData?.weight, formData?.pesoAprovechado);
      const rendStatus = getRendimientoStatus(rendimientoValue);

      const tarifa = calcularTarifa(formData?.cutType, pesoValue);
      const costeSala = calcularCosteSala(formData?.cutType, pesoValue);

      const updatePayload = {
        cliente_id: formData?.clientId,
        empleado_id: formData?.employeeId,
        producto_id: formData?.productId || null,
        peso: pesoValue,
        peso_loncheado: pesoValue,
        peso_inicial: pesoValue,
        peso_aprovechado: pesoAprovechadoValue,
        merma: mermaValue,
        rendimiento: rendimientoValue != null ? parseFloat(rendimientoValue?.toFixed(2)) : null,
        estado_rendimiento: rendStatus?.label || null,
        tipo: formData?.cutType,
        observaciones: formData?.observaciones || null,
        tarifa: tarifa,
        coste_sala: costeSala,
        lote_interno: formData?.loteInterno || DEFAULT_LOTE_INTERNO,
        lote_externo: formData?.loteExterno || null,
        emplatado: formData?.emplatado || false,
        numero_blisteres: isLasDosCliente ? (parseInt(formData?.numeroBlisteres) || 0) : (formData?.numeroBlisteres != null ? parseInt(formData?.numeroBlisteres) : null),
        // ── formato_corte: save from form selection ──
        formato_corte: formData?.formatoCorte || loncheado?.formato_corte || null,
      };

      // ALWAYS update, never insert
      const { error: updateError } = await supabase
        ?.from('loncheados')
        ?.update(updatePayload)
        ?.eq('id', loncheado?.id);

      if (updateError) throw updateError;

      // Handle photos
      const remoteFotos = formData?.fotos?.filter(f => f?.remote);
      const localFotos = formData?.fotos?.filter(f => !f?.remote && f?.file);
      let allRemoteUrls = remoteFotos?.map(f => f?.url) || [];

      if (localFotos?.length > 0) {
        for (const foto of localFotos) {
          try {
            const { publicUrl } = await rendimientoService?.uploadFoto(loncheado?.id, foto?.file);
            allRemoteUrls?.push(publicUrl);
          } catch (photoErr) {
            console.warn('Error uploading photo:', photoErr?.message);
          }
        }
      }

      if (allRemoteUrls?.length > 0) {
        const fotoUrlToSave = allRemoteUrls?.length === 1 ? allRemoteUrls?.[0] : JSON.stringify(allRemoteUrls);
        await supabase?.from('loncheados')?.update({ foto_url: fotoUrlToSave })?.eq('id', loncheado?.id);
      }

      setSuccess(true);
      setTimeout(() => {
        onSaved?.();
        onClose?.();
      }, 800);
    } catch (err) {
      setError(err?.message || 'Error al guardar el registro.');
    } finally {
      setLoading(false);
      setBtnLoading(false);
    }
  };

  const mermaRealTime = calcularMerma(formData?.weight, formData?.pesoAprovechado);
  const rendimientoRealTime = calcularRendimiento(formData?.weight, formData?.pesoAprovechado);
  const tarifaPreview = formData?.weight && parseFloat(formData?.weight) > 0 && formData?.cutType
    ? calcularTarifa(formData?.cutType, formData?.weight)
    : null;
  const tarifaDescripcion = formData?.weight && parseFloat(formData?.weight) > 0 && formData?.cutType
    ? getTarifaDescripcion(formData?.cutType, formData?.weight)
    : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-4xl max-h-[95vh] overflow-y-auto rounded-2xl"
        style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.10)' }}
        onClick={e => e?.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 sticky top-0 z-10"
          style={{ background: '#141414', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div>
            <h2 className="text-xl font-bold font-oranienbaum" style={{ color: '#D6EF4E' }}>
              Editar Registro de Loncheado
            </h2>
            <p className="text-xs font-oranienbaum text-white/40 mt-0.5">
              ID: {loncheado?.id?.substring(0, 8)}…
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10"
            style={{ color: 'rgba(255,255,255,0.5)' }}
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Success Banner */}
        {success && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-xl flex items-center gap-3"
            style={{ background: 'rgba(58,127,107,0.15)', border: '1px solid rgba(58,127,107,0.40)' }}>
            <Icon name="CheckCircle2" size={18} style={{ color: '#3A7F6B' }} />
            <p className="font-oranienbaum text-sm font-bold" style={{ color: '#3A7F6B' }}>
              Registro actualizado correctamente
            </p>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 rounded-xl flex items-start gap-3"
            style={{ background: 'rgba(224,82,82,0.10)', border: '1px solid rgba(224,82,82,0.30)' }}>
            <Icon name="AlertCircle" size={16} style={{ color: '#E05252', flexShrink: 0, marginTop: 2 }} />
            <div className="flex-1">
              <p className="font-oranienbaum text-sm font-bold" style={{ color: '#E05252' }}>Error al guardar</p>
              <p className="font-oranienbaum text-xs mt-0.5" style={{ color: 'rgba(224,82,82,0.8)' }}>{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-white/30 hover:text-white/60">
              <Icon name="X" size={14} />
            </button>
          </div>
        )}

        {/* Form Body */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left Column */}
            <div className="space-y-4">
              {/* Asignación */}
              <ERPCard title="Asignación" titleAction={null}>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Cliente *</label>
                    <ClientSelector
                      value={formData?.clientId}
                      onChange={(id) => handleChange('clientId', id)}
                      error={errors?.clientId}
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Empleado *</label>
                    <EmployeeSelector
                      value={formData?.employeeId}
                      onChange={(id) => handleChange('employeeId', id)}
                      error={errors?.employeeId}
                    />
                  </div>
                </div>
              </ERPCard>

              {/* Detalles de la Pieza */}
              <ERPCard title="Detalles de la Pieza" titleAction={null}>
                <PieceDetailsForm
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  weight={formData?.weight}
                  onWeightChange={(val) => handleChange('weight', val)}
                  cutType={formData?.cutType}
                  onCutTypeChange={(val) => handleChange('cutType', val)}
                  productId={formData?.productId}
                  onProductChange={(val) => handleChange('productId', val)}
                />
              </ERPCard>

              {/* Observaciones */}
              <ERPCard title="Observaciones" titleAction={null}>
                <textarea
                  value={formData?.observaciones}
                  onChange={e => handleChange('observaciones', e?.target?.value)}
                  placeholder="Notas adicionales sobre la pieza..."
                  rows={4}
                  className="w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2 font-oranienbaum placeholder-white/25 focus:outline-none focus:border-[#D6EF4E]/40 transition-colors resize-none"
                />
              </ERPCard>

              {/* Lotes y Trazabilidad */}
              <ERPCard title="Lotes y Trazabilidad" titleAction={null}>
                <div className="space-y-4">
                  {/* Lote Interno */}
                  <div>
                    <label className={labelCls}>Lote Interno</label>
                    <input
                      type="text"
                      value={formData?.loteInterno}
                      onChange={e => handleChange('loteInterno', e?.target?.value)}
                      placeholder="LDD-3 / 690"
                      className={inputCls}
                    />
                    <p className="text-[10px] font-oranienbaum text-white/30 mt-1">
                      Valor por defecto: LDD-3 / 690. Puede modificarse manualmente.
                    </p>
                  </div>

                  {/* Lote Externo */}
                  <div>
                    <label className={labelCls}>
                      Lote Externo
                      {generatingLote && (
                        <span className="ml-2 text-[#D6EF4E]/60 text-xs">
                          <Icon name="Loader2" size={10} className="inline animate-spin" /> Generando...
                        </span>
                      )}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={formData?.loteExterno}
                        onChange={e => handleChange('loteExterno', e?.target?.value)}
                        placeholder="LDD-YYYYMMDD-001"
                        className={`${inputCls} flex-1`}
                      />
                      <button
                        type="button"
                        onClick={() => generateLoteExternoForDate(new Date(formData?.registrationDate))}
                        disabled={generatingLote}
                        title="Regenerar lote externo"
                        className="px-3 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/80 transition-colors disabled:opacity-40"
                      >
                        <Icon name="RefreshCw" size={14} />
                      </button>
                    </div>
                    <p className="text-[10px] font-oranienbaum text-white/30 mt-1">
                      Identificador principal de trazabilidad. El administrador puede modificarlo.
                    </p>
                  </div>

                  {/* Nº de Blísteres — solo para Las Dos Doncellas */}
                  {isLasDosCliente && (
                    <div>
                      <label className={labelCls}>Nº de Blísteres</label>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formData?.numeroBlisteres}
                        onChange={e => handleChange('numeroBlisteres', e?.target?.value)}
                        placeholder="0"
                        className={inputCls}
                      />
                    </div>
                  )}

                  {/* Emplatado toggle */}
                  <div className="flex items-center gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => handleChange('emplatado', !formData?.emplatado)}
                      className={`w-10 h-5 rounded-full transition-all duration-200 relative flex-shrink-0 ${formData?.emplatado ? 'bg-[#D6EF4E]' : 'bg-white/15'}`}
                    >
                      <span
                        className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all duration-200 ${formData?.emplatado ? 'left-5' : 'left-0.5'}`}
                      />
                    </button>
                    <label className={`${labelCls} mb-0 cursor-pointer`} onClick={() => handleChange('emplatado', !formData?.emplatado)}>
                      Emplatado
                    </label>
                  </div>
                </div>
              </ERPCard>

              {/* Tarifa Preview */}
              {tarifaPreview !== null && (
                <ERPCard title="Vista Previa – Tarifa Aplicada" titleAction={null}>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(214,239,78,0.08)', border: '1px solid rgba(214,239,78,0.20)' }}>
                      <p className="text-xs font-oranienbaum text-white/50 mb-1">Tarifa</p>
                      <p className="text-2xl font-bold font-oranienbaum" style={{ color: '#D6EF4E' }}>{tarifaPreview} €</p>
                    </div>
                    <div className="rounded-xl p-4 text-center" style={{ background: 'rgba(201,162,77,0.08)', border: '1px solid rgba(201,162,77,0.20)' }}>
                      <p className="text-xs font-oranienbaum text-white/50 mb-1">Coste Sala</p>
                      <p className="text-2xl font-bold font-oranienbaum" style={{ color: '#C9A24D' }}>{calcularCosteSala(formData?.cutType, formData?.weight)} €</p>
                    </div>
                  </div>
                  {tarifaDescripcion && (
                    <div className="mt-3 px-3 py-2.5 rounded-lg flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                      <Icon name="Info" size={13} style={{ color: '#D6EF4E', flexShrink: 0 }} />
                      <p className="text-xs font-oranienbaum text-white/70">{tarifaDescripcion}</p>
                    </div>
                  )}
                </ERPCard>
              )}

              {/* Fotografía */}
              <ERPCard title="Fotografías" titleAction={null}>
                <FotografiaSection
                  fotos={formData?.fotos}
                  onChange={(fotos) => handleChange('fotos', fotos)}
                  onFotosChange={(fotos) => handleChange('fotos', fotos)}
                  loncheadoId={loncheado?.id}
                />
              </ERPCard>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Rendimiento y Merma */}
              <ERPCard title="Rendimiento y Merma" titleAction={null}>
                <RendimientoSection
                  formData={formData}
                  onChange={handleChange}
                  errors={errors}
                  pesoInicial={formData?.weight}
                  pesoAprovechado={formData?.pesoAprovechado}
                  onPesoAprovechadoChange={(val) => handleChange('pesoAprovechado', val)}
                />
              </ERPCard>

              {/* Resumen en Tiempo Real */}
              <ERPCard title="Resumen en Tiempo Real" titleAction={null}>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="rounded-lg p-3 border border-white/5" style={{ background: '#111111' }}>
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Peso Inicial</p>
                      <p className="text-lg font-bold font-oranienbaum text-white/85">
                        {formData?.weight ? `${parseFloat(formData?.weight)?.toFixed(2)} kg` : '—'}
                      </p>
                    </div>
                    <div className="rounded-lg p-3 border border-white/5" style={{ background: '#111111' }}>
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Peso Aprovechado</p>
                      <p className="text-lg font-bold font-oranienbaum text-white/85">
                        {formData?.pesoAprovechado ? `${parseFloat(formData?.pesoAprovechado)?.toFixed(2)} kg` : '—'}
                      </p>
                    </div>
                    <div className="rounded-lg p-3" style={{ background: 'rgba(224,82,82,0.08)', border: '1px solid rgba(224,82,82,0.15)' }}>
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Merma</p>
                      <p className="text-lg font-bold font-oranienbaum" style={{ color: '#E05252' }}>
                        {mermaRealTime != null ? `${parseFloat(mermaRealTime)?.toFixed(2)} kg` : '—'}
                      </p>
                    </div>
                    <div
                      className="rounded-lg p-3"
                      style={{
                        background: rendimientoRealTime >= 70 ? 'rgba(58,127,107,0.08)' : rendimientoRealTime > 0 ? 'rgba(201,162,77,0.08)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${rendimientoRealTime >= 70 ? 'rgba(58,127,107,0.15)' : rendimientoRealTime > 0 ? 'rgba(201,162,77,0.15)' : 'rgba(255,255,255,0.05)'}`
                      }}
                    >
                      <p className="font-oranienbaum text-white mb-1 text-[17px]">Rendimiento</p>
                      <p className="text-lg font-bold font-oranienbaum"
                        style={{ color: rendimientoRealTime >= 70 ? '#3A7F6B' : rendimientoRealTime > 0 ? '#C9A24D' : 'rgba(255,255,255,0.85)' }}>
                        {rendimientoRealTime != null && rendimientoRealTime > 0 ? `${rendimientoRealTime?.toFixed(1)}%` : '—'}
                      </p>
                    </div>
                  </div>
                  {rendimientoRealTime > 0 && (
                    <div className="mt-2">
                      <div className="flex justify-between text-[10px] font-oranienbaum text-white/40 mb-1">
                        <span>Rendimiento</span>
                        <span>{rendimientoRealTime?.toFixed(1)}%</span>
                      </div>
                      <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${Math.min(rendimientoRealTime, 100)}%`,
                            background: rendimientoRealTime >= 70 ? '#3A7F6B' : rendimientoRealTime >= 50 ? '#C9A24D' : '#E05252'
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </ERPCard>

              {/* Fecha y Hora */}
              <ERPCard title="Fecha y Hora del Registro" titleAction={null}>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Fecha</label>
                    <input
                      type="date"
                      value={formData?.registrationDate}
                      onChange={e => handleChange('registrationDate', e?.target?.value)}
                      className="w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
                    />
                  </div>
                  <div>
                    <label className={labelCls}>Hora</label>
                    <input
                      type="time"
                      value={formData?.registrationTime}
                      onChange={e => handleChange('registrationTime', e?.target?.value)}
                      className="w-full bg-[#111111] border border-white/10 text-white/85 text-sm rounded-lg px-3 py-2.5 font-oranienbaum focus:outline-none focus:border-[#D6EF4E]/40 transition-colors"
                    />
                  </div>
                </div>
              </ERPCard>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 sticky bottom-0"
          style={{ background: '#141414', borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <ERPButton variant="ghost" icon="X" onClick={onClose} disabled={loading}>
            Cancelar
          </ERPButton>
          <ERPButton variant="primary" icon="Save" onClick={handleSave} disabled={loading || success}>
            {success ? 'Guardado ✓' : 'Guardar Cambios'}
          </ERPButton>
        </div>
      </div>
    </div>
  );
};

export default LoncheadosEditModal;